import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
// @mui
import {
    Box,
    Card,
    Table,
    TableBody,
    Container,
    TableContainer,
    Typography,
    TableRow,
    TableCell,
} from '@mui/material';

import { useAuthContext } from '../../auth/useAuthContext';
import { useDispatch, useSelector } from '../../redux/store';
// components
import Scrollbar from '../../components/scrollbar';
import {
    useTable,
    getComparator,
    TableHeadCustom,
    TablePaginationCustom,
} from '../../components/table';
import ItemNotFound from '../../components/ItemNotFound';
// sections
import { UserTableToolbar, UserTableRow } from '../../sections/user/list';
import { viewProgramme, viewProgrammeMarking } from '../../redux/slices/programme';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
    { id: 'FirstName', label: 'Name', align: 'left', width: '25%' },
    { id: 'Contact', label: 'Contact', align: 'left', width: '25%' },
    { id: 'TotalPoint', label: 'Total Marks', align: 'center', width: '15%' },
    { id: 'FeePaid', label: 'Fee Paid', align: 'center', width: '10%' },
    { id: 'CertificateAward', label: 'Certificate Award', align: 'center', width: '20%' },
    { id: '', label: '', align: 'left', width: '5%' },
];

// ----------------------------------------------------------------------

export default function UserListPage() {
    const {
        dense,
        page,
        order,
        orderBy,
        rowsPerPage,
        setPage,
        //
        selected,
        //
        onSort,
        onChangeDense,
        onChangePage,
        onChangeRowsPerPage,
    } = useTable();

    const { user } = useAuthContext();

    const dispatch = useDispatch();

    const { programmeList, programmeMarking } = useSelector((state) => state.programme);

    const [tableData, setTableData] = useState([]);

    const [filterName, setFilterName] = useState('');

    const [filterStartDate, setFilterStartDate] = useState(null);

    const [filterEndDate, setFilterEndDate] = useState(null);

    const [openRow, setOpenRow] = useState([]);

    useEffect(() => {
        dispatch(viewProgramme());
        dispatch(viewProgrammeMarking(0));
    }, [dispatch, user]);

    useEffect(() => {
        if (programmeMarking) {
            setTableData(programmeMarking);
        }
    }, [dispatch, programmeMarking]);

    const combineRowsByUserID = (rows) => {
        const combinedRows = {};

        rows.forEach((row) => {
            const { UserID } = row;
            if (!combinedRows[UserID]) {
                combinedRows[UserID] = { ...row };
            } else {
                if (!Array.isArray(combinedRows[UserID])) {
                    combinedRows[UserID] = [combinedRows[UserID]];
                }
                combinedRows[UserID].push(row);
            }
        });

        return Object.values(combinedRows);
    };

    const combinedTableData = combineRowsByUserID(tableData);

    const dataFiltered = applyFilter({
        inputData: combinedTableData,
        comparator: getComparator(order, orderBy),
        filterName,
        filterStartDate,
        filterEndDate,
    });

    const isFiltered = filterName !== '' || (!!filterStartDate && !!filterEndDate);

    const handleFilterName = (event) => {
        setPage(0);
        setFilterName(event.target.value);
    };

    const handleResetFilter = () => {
        setFilterName('');
        setFilterEndDate(null);
        setFilterStartDate(null);
    };

    const handleToggle = (userID) => {
        setOpenRow(openRow === userID ? null : userID);
    };

    return (
        <>
            <Helmet>
                <title>MAGScholar | User List</title>
            </Helmet>

            <Box sx={{ position: 'relative' }}>
                <Container>
                    <Card>
                        <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: 'rgb(243, 108, 79)' }}>
                            <Typography variant='h6' color="#fff">Certified MAGScholar© Programme</Typography>
                        </Box>

                        <UserTableToolbar
                            filterName={filterName}
                            onFilterName={handleFilterName}
                            filterEndDate={filterEndDate}
                            filterStartDate={filterStartDate}
                            onFilterStartDate={(newValue) => {
                                setFilterStartDate(newValue);
                            }}
                            onFilterEndDate={(newValue) => {
                                setFilterEndDate(newValue);
                            }}
                            isFiltered={isFiltered}
                            onResetFilter={handleResetFilter}
                        />

                        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                            <Scrollbar>
                                <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
                                    <TableHeadCustom
                                        order={order}
                                        orderBy={orderBy}
                                        headLabel={TABLE_HEAD}
                                        rowCount={tableData.length}
                                        numSelected={selected.length}
                                        onSort={onSort}
                                    />

                                    <TableBody>
                                        {dataFiltered.length > 0 ? (
                                            dataFiltered
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row, index) => (
                                                    <UserTableRow
                                                        key={index}
                                                        row={row}
                                                        selected={selected.includes(row.UserID)}
                                                        programmeList={programmeList}
                                                        openRow={openRow}
                                                        handleToggle={handleToggle}
                                                    />
                                                ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={programmeList.length + 2} align="center">
                                                    <ItemNotFound title="No Data Available" />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Scrollbar>
                        </TableContainer>

                        <TablePaginationCustom
                            count={dataFiltered.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={onChangePage}
                            onRowsPerPageChange={onChangeRowsPerPage}
                            dense={dense}
                            onChangeDense={onChangeDense}
                        />
                    </Card>
                </Container>
            </Box >
        </>
    );
}

// ----------------------------------------------------------------------

function applyFilter({ inputData, comparator, filterName, filterStartDate, filterEndDate }) {
    const stabilizedThis = inputData.map((el, index) => [el, index]);

    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });

    let sortedData = stabilizedThis.map((el) => el[0]);

    if (filterName) {
        sortedData = sortedData.filter((user) => {
            if (Array.isArray(user)) {
                return user.some(
                    (entry) =>
                        (entry.FirstName && entry.FirstName.toLowerCase().includes(filterName.toLowerCase())) ||
                        (entry.LastName && entry.LastName.toLowerCase().includes(filterName.toLowerCase())) ||
                        (entry.UserEmailAddress && entry.UserEmailAddress.toLowerCase().includes(filterName.toLowerCase())) ||
                        (entry.UserContactNo && entry.UserContactNo.includes(filterName))
                );
            }
            return (
                (user.FirstName && user.FirstName.toLowerCase().includes(filterName.toLowerCase())) ||
                (user.LastName && user.LastName.toLowerCase().includes(filterName.toLowerCase())) ||
                (user.UserEmailAddress && user.UserEmailAddress.toLowerCase().includes(filterName.toLowerCase())) ||
                (user.UserContactNo && user.UserContactNo.includes(filterName))
            );
        });
    }

    if (filterStartDate && filterEndDate) {
        sortedData = sortedData.map((user) => {
            if (Array.isArray(user)) {
                return user.filter(
                    (entry) =>
                        entry.Year >= filterStartDate.getFullYear() &&
                        entry.Year <= filterEndDate.getFullYear()
                );
            }
            if (
                user.Year >= filterStartDate.getFullYear() &&
                user.Year <= filterEndDate.getFullYear()
            ) {
                return user;
            }
            return null;
        }).filter((user) => user !== null && user.length !== 0); // Remove nulls and empty arrays
    }

    return sortedData;
}
